/**
 * Floating void decor renderer (M18b): one InstancedMesh per (pieceId,
 * submesh), flat in the segment group so the spawn/exit waves animate
 * instances exactly like island decor (`animInstances`), plus a per-frame
 * FLOAT pass — bob (amp·sin over sim time) and slow drift — that owns the
 * instance matrices only AFTER the assemble wave finished (the session
 * gates tick() on its spawning/exiting sets; a 2s envelope ramps the bob
 * in so the handoff never pops).
 *
 * Void decor never collides (the generator keeps it out of `placements`),
 * never casts shadows (the sun fits the track box), and never occludes
 * the camera (a drifting shoal must not yank the boom): castShadow off,
 * no `occluder` flag, and every mesh carries `userData.voidDecor` so the
 * session's fit box skips it (far-band silhouettes would inflate focus
 * and shadow fit otherwise).
 */
import * as THREE from "three";
import type { Placement } from "../shared/types.ts";
import type { Assets } from "./assets.ts";

interface FloatParams {
  amp: number;
  period: number;
  phase: number;
  drift: readonly [number, number, number] | null;
}

interface AnimInstances {
  base: THREE.Matrix4[];
  worldPos: THREE.Vector3[];
}

export interface FloatBuild {
  meshes: THREE.InstancedMesh[];
  /** Advance the float pass to sim time t (seconds). First call stamps
   * the epoch: bob eases in over ~2s from there, drift time is
   * segment-local (bounded — a long session never walks a far shoal
   * across the track). */
  tick(simT: number): void;
  dispose(): void;
}

const tmpM = new THREE.Matrix4();
const RAMP_SECONDS = 2;
/** Drift displacement saturates after this many seconds. */
const DRIFT_CAP_SECONDS = 45;

/** Submeshes of a template with root-relative transforms (the
 * islandRender walk, minus foliage classification). */
function subMeshesOf(
  template: THREE.Object3D,
): { geometry: THREE.BufferGeometry; material: THREE.Material; local: THREE.Matrix4 }[] {
  const out: {
    geometry: THREE.BufferGeometry;
    material: THREE.Material;
    local: THREE.Matrix4;
  }[] = [];
  const walk = (node: THREE.Object3D, parent: THREE.Matrix4) => {
    node.updateMatrix();
    const local = node === template
      ? new THREE.Matrix4()
      : new THREE.Matrix4().multiplyMatrices(parent, node.matrix);
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh) {
      out.push({
        geometry: mesh.geometry,
        material: mesh.material as THREE.Material,
        local,
      });
    }
    for (const child of node.children) walk(child, local);
  };
  walk(template, new THREE.Matrix4());
  return out;
}

export function buildVoidDecor(
  placements: readonly Placement[],
  assets: Assets,
  group: THREE.Group,
  missing: Set<string>,
): FloatBuild | null {
  if (placements.length === 0) return null;

  const byPiece = new Map<string, Placement[]>();
  for (const p of placements) {
    const list = byPiece.get(p.pieceId) ?? [];
    list.push(p);
    byPiece.set(p.pieceId, list);
  }

  const meshes: THREE.InstancedMesh[] = [];
  const floatParams: FloatParams[][] = [];
  const tmpP = new THREE.Vector3();
  const tmpQ = new THREE.Quaternion();
  const tmpS = new THREE.Vector3();

  for (const [pieceId, list] of byPiece) {
    const template = assets.templates.get(pieceId);
    if (!template) {
      missing.add(pieceId);
      continue;
    }
    for (const sub of subMeshesOf(template)) {
      const imesh = new THREE.InstancedMesh(
        sub.geometry,
        sub.material,
        list.length,
      );
      imesh.castShadow = false;
      imesh.receiveShadow = false;
      const anim: AnimInstances = { base: [], worldPos: [] };
      const params: FloatParams[] = [];
      list.forEach((p, i) => {
        const s = p.scale ?? 1;
        tmpP.set(p.pos[0], p.pos[1], p.pos[2]);
        tmpQ.setFromAxisAngle(
          new THREE.Vector3(0, 1, 0),
          p.rotation * Math.PI / 2,
        );
        tmpS.set(s, s, s);
        tmpM.compose(tmpP, tmpQ, tmpS);
        tmpM.multiply(sub.local);
        imesh.setMatrixAt(i, tmpM);
        anim.base.push(tmpM.clone());
        anim.worldPos.push(tmpP.clone());
        const f = p.float;
        params.push({
          amp: f?.amp ?? 0,
          period: f?.period ?? 8,
          phase: f?.phase ?? 0,
          drift: f?.drift ?? null,
        });
      });
      imesh.instanceMatrix.needsUpdate = true;
      imesh.computeBoundingSphere();
      // Bounding sphere must survive the bob/drift walk or instances
      // vanish to frustum culling at the offset extremes.
      if (imesh.boundingSphere) imesh.boundingSphere.radius += 12;
      imesh.userData.animInstances = anim;
      imesh.userData.voidDecor = true;
      group.add(imesh);
      meshes.push(imesh);
      floatParams.push(params);
    }
  }

  if (meshes.length === 0) return null;

  let epoch: number | null = null;
  return {
    meshes,
    tick(simT: number): void {
      if (epoch === null) epoch = simT;
      const local = simT - epoch;
      const env = Math.min(1, local / RAMP_SECONDS);
      const driftT = Math.min(local, DRIFT_CAP_SECONDS);
      for (let m = 0; m < meshes.length; m++) {
        const mesh = meshes[m];
        const anim = mesh.userData.animInstances as AnimInstances;
        const params = floatParams[m];
        for (let i = 0; i < params.length; i++) {
          const f = params[i];
          const bob = f.amp * env *
            Math.sin((2 * Math.PI * (simT + f.phase)) / f.period);
          tmpM.copy(anim.base[i]);
          tmpM.elements[13] += bob;
          if (f.drift) {
            tmpM.elements[12] += f.drift[0] * driftT;
            tmpM.elements[13] += f.drift[1] * driftT;
            tmpM.elements[14] += f.drift[2] * driftT;
          }
          mesh.setMatrixAt(i, tmpM);
        }
        mesh.instanceMatrix.needsUpdate = true;
      }
    },
    dispose(): void {
      for (const m of meshes) m.dispose(); // instance buffers only
    },
  };
}
